import { useContext, useEffect, useRef, useState, useMemo } from "react"
import HRDashboardCard from "./HRDashboardCard"
import HRDashboardDayofWeek from "./HRDashboardDayOfWeek"
import { classNames } from "../App"
import CloseIcon from "../icons/close"
import CustomDataTable from "../components/dataTable"
import EditIcon from "../icons/edit"
import useSWR from "swr"
import { hrCancelSlotEndpoint, hrCustomerAssignToSlotEndpoint, hrGetBaseStatsEndpoint, hrGetSlotsForDashboardEndpoint, hrListGetAssignableCustomerEndpoint } from "../hr_endpoints"
import { UserDataContext } from "../context/UserContextProvider"
import { ToastDataContext } from "../context/ToastContextProvider"
import dayjs from "dayjs"
import SearchSelect from "../components/searchselect"
import CustomModal from "../components/modal"
import { useForm } from "react-hook-form"
import CustomTextInput from "../components/textInput"
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next"
import GuestAddIcon from "../icons/guestadd"
import HRDashboardListSlotListGuestAddModal from "./HRDashboardListSlotListGuestAddModal"
import HRFeedbackForm from "./HRFeedbackForm"
import { QRCode } from 'react-qrcode-logo';
import { qrQuietZone } from '../utils';
import QRPCIcon from "../icons/qrpc"
import HRDashboardListInfo from "./HRDashboardListInfo"

function useQRSize() {
    const [size, setSize] = useState(256);
    useEffect(() => {
        const update = () => setSize(Math.min(256, Math.max(160, typeof window !== 'undefined' ? window.innerWidth - 80 : 256)));
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    return size;
}

// The timezone plugins are registered here rather than inside the component so
// helpers at module scope can rely on dayjs.tz without depending on a render
// having happened first.
dayjs.extend(timezone)
dayjs.extend(utc)

// Mirrors models.TIME_BEFORE_SLOT_START_FOR_CANCEL. HR follows the employee
// rule: cancelling closes five minutes before the appointment, so the control
// comes off the row rather than failing when pressed.
const CANCEL_DEADLINE_MINUTES = 5

// start_time is Romanian wall clock, the same reading the backend takes.
const canStillCancel = (slot) =>
    dayjs.tz(slot.start_time, "Europe/Bucharest")
        .subtract(CANCEL_DEADLINE_MINUTES, "minute")
        .isAfter(dayjs())

export default function HRDashboard({ selectedWeek }) {

    const { t } = useTranslation();

    dayjs.locale('ro');
    dayjs.extend(timezone);
    dayjs.extend(utc);

    const userContext = useContext(UserDataContext)
    const toastContext = useContext(ToastDataContext)

    const [days, setDays] = useState([
        {
            index: 1,
            name: t("dashboard.days.monday"),
            disabled: false
        },
        {
            index: 2,
            name: t("dashboard.days.tuesday"),
            disabled: false
        },
        {
            index: 3,
            name: t("dashboard.days.wednesday"),
            disabled: false
        },
        {
            index: 4,
            name: t("dashboard.days.thursday"),
            disabled: false
        },
        {
            index: 5,
            name: t("dashboard.days.friday"),
            disabled: false
        },
        {
            index: 6,
            name: t("dashboard.days.saturday"),
            disabled: false
        },
        {
            index: 0,
            name: t("dashboard.days.sunday"),
            disabled: false
        }
    ])

    const [selectedDay, setSelectedDay] = useState(null)
    const [isClientEditable, setIsClientEditable] = useState(null)
    const [oldCustomerID, setOldCustomerID] = useState(null)
    const [selectedList, setSelectedList] = useState(null)
    const [open, setOpen] = useState(false)
    const [activeSlot, setActiveSlot] = useState(null)
    const [openQR, setOpenQR] = useState(false)
    const [selectedSlotForQR, setSelectedSlotForQR] = useState(null)
    const qrSize = useQRSize()

    const clearState = () => {
        setTimeout(() => {
            setActiveSlot(null)
        }, 500)
    }

    const { register, reset, handleSubmit } = useForm();

    const onDaySelect = (index) => {
        setSelectedDay(index)
    }

    const formRef = useRef();
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const onSubmit = (data) => {
        onSlotCancel(data);
        setOpen(false);
        reset();
    }

    const onCancel = () => {
        setOpen(false);
        clearState();
        reset()
    }

    const onSlotCancel = async (data) => {
        const response = await fetch(hrCancelSlotEndpoint(activeSlot), {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${userContext.user?.token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ reason: data.reason })
        })

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t("dashboard.sessionExpired"));
        }

        if (!response.ok) {
            toastContext.error(await response.text());
        } else {
            toastContext.success(t("dashboard.slotCancelled"));
            mutateSlots()
            mutateBaseStats()
        }
    }

    const { data: baseStats, error: baseStatsError, mutate: mutateBaseStats } = useSWR([hrGetBaseStatsEndpoint(selectedWeek)])
    const { data: slots, error: slotsError, mutate: mutateSlots } = useSWR(selectedDay || selectedDay === 0 ? [hrGetSlotsForDashboardEndpoint(selectedDay, selectedWeek)] : null)
    const { data: customers, error: customerError } = useSWR(selectedList ? [hrListGetAssignableCustomerEndpoint(selectedList)] : null);

    // Default order: group by list (ordered by list start date), then by time within each list.
    // So a list starting on 15th 9PM appears before a list starting on 16th 4AM.
    const sortedSlots = useMemo(() => {
        if (!slots) return [];
        return [...slots].sort((a, b) => {
            const listDateA = (a.list_start_date || '').toString();
            const listDateB = (b.list_start_date || '').toString();
            const dateCmp = listDateA.localeCompare(listDateB);
            if (dateCmp !== 0) return dateCmp;
            const listCmp = (a.list_id || 0) - (b.list_id || 0);
            if (listCmp !== 0) return listCmp;
            return dayjs(a.start_time).unix() - dayjs(b.start_time).unix();
        });
    }, [slots]);

    // Open QR modal when landing with ?slotId= (e.g. from SMS reminder link)
    const slotIdFromUrl = searchParams.get('slotId');
    useEffect(() => {
        if (!slotIdFromUrl || !sortedSlots.length) return;
        const slotIdNum = parseInt(slotIdFromUrl, 10);
        if (Number.isNaN(slotIdNum)) return;
        // Match the same conditions as the QR button: only HR-claimed slots with a validation_token
        const slot = sortedSlots.find(s => s.id === slotIdNum && s.is_hr_claimed && s.validation_token);
        if (slot) {
            setSelectedSlotForQR(slot);
            setOpenQR(true);
            const next = new URLSearchParams(searchParams);
            next.delete('slotId');
            setSearchParams(next, { replace: true });
        }
    }, [slotIdFromUrl, sortedSlots, searchParams, setSearchParams]);


    useEffect(() => {
        if (baseStatsError) {
            toastContext.error(baseStatsError.message)
        }
        if (slotsError) {
            toastContext.error(slotsError.message)
        }
        if (customerError) {
            toastContext.error(customerError.message)
        }
    }, [baseStatsError, slotsError, customerError])

    useEffect(() => {
        setDays((currentValue) => {
            return currentValue.map((day) => {
                return {
                    ...day,
                    disabled: !baseStats?.available_day_indexes?.includes(day.index)
                }
            })
        })
        setSelectedDay(baseStats?.available_day_indexes?.[0])
    }, [baseStats])

    const [guestAddModalOpen, setGuestAddModalOpen] = useState(false)

    const onGuestConclusion = () => {
        mutateSlots()
        mutateBaseStats()
        setIsClientEditable(null)
    }

    const onClientSelect = async (client) => {
        const response = await fetch(hrCustomerAssignToSlotEndpoint(client.id, selectedList, isClientEditable, oldCustomerID || ""), {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${userContext.user?.token}`,
                "Content-Type": "application/json"
            }
        })

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t("dashboard.sessionExpired"));
        }

        if (!response.ok) {
            const error = await response.text();
            if (error.includes("WRONG_USER")) {
                toastContext.error(t("dashboard.staleDataError", { name: error.split("|")[1] }));
            } else {
                toastContext.error(error);
            }
        } else {
            toastContext.success(t("dashboard.employeeAssigned"));
            mutateSlots()
            mutateBaseStats()
        }

        setIsClientEditable(null)
    }

    const stats = [
        {
            title: t("dashboard.reservedSlots"),
            value: baseStats?.reserved_slots,
            comparedToText: t("dashboard.thisWeek"),
        },
        {
            title: t("dashboard.availableSlots"),
            value: baseStats?.available_slots,
            comparedToText: t("dashboard.thisWeek"),
        },
    ]

    // Helper function to ensure HR-claimed slots always sort first
    const hrPrioritySort = (rowA, rowB, sortFn) => {
        // HR-claimed slots always first
        if (rowA.original.is_hr_claimed && !rowB.original.is_hr_claimed) return -1;
        if (!rowA.original.is_hr_claimed && rowB.original.is_hr_claimed) return 1;
        // If both are HR-claimed or both are not, use the provided sort function
        return sortFn ? sortFn(rowA, rowB) : 0;
    };

    const columns = [
        {
            accessorKey: "id", // Matches the key in your data
            header: "ID",
            visibleTo: 'tablet',
            sortingFn: (rowA, rowB) => hrPrioritySort(rowA, rowB, (a, b) => a.original.id - b.original.id)
        },
        {
            accessorKey: "therapist",
            header: t("dashboard.therapist"),
            visibleTo: 'mobile',
            sortingFn: (rowA, rowB) => hrPrioritySort(rowA, rowB, (a, b) => {
                const therapistA = a.original.therapist || '';
                const therapistB = b.original.therapist || '';
                return therapistA.localeCompare(therapistB);
            })
        },
        {
            accessorKey: "customer",
            header: t("dashboard.employee"),
            sortingFn: (rowA, rowB) => hrPrioritySort(rowA, rowB, (a, b) => {
                const customerA = a.original.customer || '';
                const customerB = b.original.customer || '';
                return customerA.localeCompare(customerB);
            }),
            cell: ({ row }) => (
                <div className="flex flex-row gap-2 items-center">
                    {isClientEditable !== row.original.id ? (
                        <div className="flex flex-row gap-2 items-center">
                            {row.original.is_hr_claimed && (
                                <span className="badge badge-primary badge-sm">HR</span>
                            )}
                            <span>{row.original.customer}</span>
                            {
                                ['Deschis', 'Rezervat'].includes(row.original.status) && !row.original.is_hr_claimed && (<span onClick={() => {
                                    setIsClientEditable(row.original.id)
                                    setSelectedList(row.original.list_id)
                                    setOldCustomerID(row.original.customer_id)
                                }}>
                                    <EditIcon className="size-4 cursor-pointer mt-1" />
                                </span>)
                            }
                        </div>
                    ) : (
                        <div>
                            <SearchSelect values={customers} onSelect={onClientSelect} defaultValue={row.original.customer} />
                            <span onClick={() => {
                                setGuestAddModalOpen(true);
                            }}>
                                <GuestAddIcon className="size-8 cursor-pointer mt-1" />
                            </span>
                        </div>
                    )}
                </div>
            )
        },
        {
            accessorKey: "time",
            header: t("dashboard.time"),
            cell: ({ row }) => (
                <div className="flex flex-row gap-2">
                    <span> {dayjs.tz(row.original.start_time, 'Europe/Bucharest').format('HH:mm')} - {dayjs.tz(row.original.end_time, 'Europe/Bucharest').format('HH:mm')} </span>
                </div>
            ),
            sortingFn: (rowA, rowB) => {
                // HR-claimed slots always first
                if (rowA.original.is_hr_claimed && !rowB.original.is_hr_claimed) return -1;
                if (!rowA.original.is_hr_claimed && rowB.original.is_hr_claimed) return 1;
                // Then sort by time
                return dayjs(rowA.original.start_time).unix() - dayjs(rowB.original.start_time).unix()
            }
        },
        {
            accessorKey: "room_info",
            header: t("dashboard.roomInfo"),
            visibleTo: 'tablet',
            cell: ({ row }) => (
                <span>{row.original.room_info || '-'}</span>
            ),
            sortingFn: (rowA, rowB) => hrPrioritySort(rowA, rowB, (a, b) => {
                const roomA = a.original.room_info || '';
                const roomB = b.original.room_info || '';
                return roomA.localeCompare(roomB);
            })
        },
        {
            accessorKey: "status",
            header: t("dashboard.status.name"),
            cell: ({ row }) => (
                <div className={
                    classNames(
                        "badge mt-2",
                        row.original.status === "Rezervat" && "bg-primary",
                        row.original.status === "Anulat" && "badge-error",
                        row.original.status === "Deschis" && "badge-success"
                    )
                }>
                    {t(`dashboard.status.${row.original.status}`)}
                </div>
            ),
            sortingFn: (rowA, rowB) => hrPrioritySort(rowA, rowB, (a, b) => {
                const statusA = a.original.status || '';
                const statusB = b.original.status || '';
                return statusA.localeCompare(statusB);
            })
        },
        {
            accessorKey: "actions",
            header: t("dashboard.actions"),
            cell: ({ row }) => (
                <div className="flex flex-row gap-2 items-center">
                    {row.original.is_hr_claimed && row.original.validation_token && (
                        <span onClick={() => {
                            setSelectedSlotForQR(row.original)
                            setOpenQR(true)
                        }}>
                            <QRPCIcon className={"size-7 cursor-pointer text-primary"} />
                        </span>
                    )}
                    {!['Anulat', 'Finalizat'].includes(row.original.status) && canStillCancel(row.original) && <span onClick={() => {
                        setActiveSlot(row.original.id)
                        setOpen(true)
                    }}><CloseIcon className={"size-7 cursor-pointer"} /></span>}
                </div>
            )
        },
    ]

    const [pagination, setPagination] = useState({
        pageIndex: 0, // Current page index (zero-based)
        pageSize: 100, // Number of rows per page
    });

    // Default order is list (by start date) then time — applied in sortedSlots; no column sort so that order is kept
    const [sorting, setSorting] = useState([])

    return (
        <>
            <div className="flex flex-col gap-4">
                <div className="stats shadow flex flex-col sm:flex-row">
                    {stats.map((stats, index) => (
                        <HRDashboardCard key={index} {...stats} />
                    ))}
                </div>
                <HRDashboardDayofWeek days={days} onDaySelect={onDaySelect} selectedDay={selectedDay} />
                <HRDashboardListInfo slots={sortedSlots} onChanged={mutateSlots} />
                <CustomDataTable
                    data={sortedSlots}
                    columns={columns}
                    pagination={pagination}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    onPaginationChange={setPagination}
                    getRowClassName={(rowData) => {
                        return rowData?.is_hr_claimed ? "bg-primary/10 border-l-4 border-l-primary" : "";
                    }}
                />
            </div>
            <CustomModal open={open} setOpen={setOpen} title={t("dashboard.slotDeactivation")} cancelButton={t("dashboard.cancel")} confirmButton={t("dashboard.deactivate")} onConfirm={() => {
                formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }} onCancel={onCancel} noOutsideClick={true} withIcon={false}>
                <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex flex-row gap-2 flex-wrap">
                        <div>
                            <label htmlFor="reason" className="block font-subtitle text-neutral-1100 mb-2">{t("dashboard.reason")}</label>
                            <CustomTextInput register={register('reason')} placeholder={t("dashboard.reasonPlaceholder")} className="w-full max-w-[25rem]" />
                        </div>
                    </div>
                </form>
            </CustomModal>
            <HRDashboardListSlotListGuestAddModal open={guestAddModalOpen} setOpen={setGuestAddModalOpen} onGuestConclusion={onGuestConclusion} activeSlotId={isClientEditable} />
            <CustomModal open={openQR} setOpen={setOpenQR} title={t("dashboard.showQR") || "Afișează codul QR"} cancelButton={t("dashboard.cancel")} confirmMode={true} onConfirmModeButton={() => setOpenQR(false)} Icon={QRPCIcon}>
                <div className="flex flex-col items-center w-full max-w-full overflow-hidden mb-6">
                    <div className="w-full flex justify-center min-w-0 px-1">
                        <QRCode value={selectedSlotForQR?.validation_token || ''} size={qrSize} quietZone={qrQuietZone(qrSize)} />
                    </div>
                    <p className="text-center mt-4 px-2">
                        {t("dashboard.presentQR") || "Prezentați codul QR de mai sus pentru finalizare confirmare"}
                    </p>
                </div>
            </CustomModal>
            <HRFeedbackForm />
        </>
    )
}